/**
 * O-1B Visa Criteria Definitions
 *
 * The O-1B is a nonimmigrant visa for individuals with extraordinary ability
 * in the arts or demonstrated record of extraordinary achievement in the
 * motion picture or television industry.
 *
 * Legal Reference: 8 CFR 214.2(o), https://www.uscis.gov/policy-manual/volume-2-part-m-chapter-4
 */

import { VisaCriterion } from './types';

/**
 * O-1B visa criteria
 *
 * The beneficiary must EITHER:
 * - Have received or been nominated for a significant national or international award
 *   (such as an Academy Award, Emmy, Grammy, or Director's Guild Award)
 *
 * OR meet at least 3 out of 6 criteria below
 */
export const O1B_CRITERIA: VisaCriterion[] = [
  {
    id: 'o1b_lead_starring_role',
    name: 'Lead or Starring Role',
    description: 'Evidence that the beneficiary has performed, and will perform, services as a lead or starring participant in productions or events which have a distinguished reputation as evidenced by critical reviews, advertisements, publicity releases, publications, contracts, or endorsements',
    weight: 9,
    requiredDocuments: [
      'CONTRACT',
      'CRITICAL_REVIEW',
      'PUBLICITY_RELEASE',
      'ENDORSEMENT'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iv)(A)',
    minimumEvidence: 3
  },
  {
    id: 'o1b_national_recognition',
    name: 'National or International Recognition',
    description: 'Evidence that the beneficiary has achieved national or international recognition for achievements evidenced by critical reviews or other published materials by or about the beneficiary in major newspapers, trade journals, magazines, or other publications',
    weight: 9,
    requiredDocuments: [
      'MEDIA_ARTICLE',
      'CRITICAL_REVIEW',
      'PUBLICATION'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iv)(B)',
    minimumEvidence: 3
  },
  {
    id: 'o1b_critical_role',
    name: 'Critical Role for Distinguished Organizations',
    description: 'Evidence that the beneficiary has performed, and will perform, in a lead, starring, or critical role for organizations and establishments that have a distinguished reputation evidenced by articles in newspapers, trade journals, publications, or testimonials',
    weight: 8,
    requiredDocuments: [
      'EMPLOYMENT_CONTRACT',
      'CRITICAL_ROLE_DOCUMENT',
      'ORGANIZATIONAL_REPUTATION_EVIDENCE'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iv)(C)',
    minimumEvidence: 2
  },
  {
    id: 'o1b_commercial_success',
    name: 'Record of Commercial or Critical Success',
    description: 'Evidence that the beneficiary has a record of major commercial or critically acclaimed successes as evidenced by such indicators as title, rating, standing in the field, box office receipts, motion pictures or television ratings, and other occupational achievements reported in trade journals, major newspapers, or other publications',
    weight: 9,
    requiredDocuments: [
      'BOX_OFFICE_RECEIPTS',
      'RATINGS_REPORT',
      'SALES_RECORDS',
      'MEDIA_COVERAGE'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iv)(D)',
    minimumEvidence: 2
  },
  {
    id: 'o1b_significant_recognition',
    name: 'Significant Recognition from Experts',
    description: 'Evidence that the beneficiary has received significant recognition for achievements from organizations, critics, government agencies, or other recognized experts in the field. Testimonials must clearly indicate the author\'s authority, expertise, and knowledge of the beneficiary\'s achievements',
    weight: 8,
    requiredDocuments: [
      'RECOMMENDATION_LETTER',
      'EXPERT_TESTIMONIAL',
      'AWARD_CERTIFICATE'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iv)(E)',
    minimumEvidence: 3
  },
  {
    id: 'o1b_high_salary',
    name: 'High Salary or Remuneration',
    description: 'Evidence that the beneficiary has either commanded a high salary or will command a high salary or other substantial remuneration for services in relation to others in the field, as evidenced by contracts or other reliable evidence',
    weight: 7,
    requiredDocuments: [
      'EMPLOYMENT_CONTRACT',
      'SALARY_STATEMENT',
      'TAX_RETURN'
    ],
    legalReference: '8 CFR 214.2(o)(3)(iv)(F)',
    minimumEvidence: 2
  }
];

/**
 * Get O-1B criteria configuration
 */
export const O1B_CONFIG = {
  visaType: 'O-1B',
  criteria: O1B_CRITERIA
};
